import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  CalendarDaysIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserGroupIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CogIcon,
  ChartBarIcon,
  CalendarIcon,
  UserIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
  XMarkIcon,
  DocumentTextIcon,
  ClockIcon as ClockOutlineIcon,
  UsersIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleSolidIcon,
  XCircleIcon as XCircleSolidIcon,
  ClockIcon as ClockSolidIcon
} from '@heroicons/react/24/solid';
import { Helmet } from 'react-helmet-async';
import {
  useGetLeaveRequestsQuery,
  useGetLeaveTypesQuery,
  useGetLeaveBalancesQuery,
  useCreateLeaveRequestMutation,
  useUpdateLeaveRequestMutation,
  useCancelLeaveRequestMutation,
  useApproveLeaveRequestMutation,
  useRejectLeaveRequestMutation,
  useCreateLeaveTypeMutation,
  useUpdateLeaveTypeMutation,
  useAccrueLeavesMutation,
  useGetLeaveCalendarQuery
} from '../../services/leaveApi';
import {
  useGetHolidaysForCalendarQuery
} from '../../services/holidayApi';

const LeaveManagement = () => {
  const { t } = useTranslation(['common', 'leave']);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  // State management
  const [activeTab, setActiveTab] = useState('requests');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentLeaveBalance, setCurrentLeaveBalance] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    leaveType: '',
    startDate: '',
    endDate: ''
  });

  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showLegend, setShowLegend] = useState(false);

  // Form states
  const [leaveForm, setLeaveForm] = useState({
    leaveTypeId: '',
    fromDate: '',
    toDate: '',
    reason: '',
    contactDuringLeave: {
      phone: '',
      email: '',
      address: ''
    }
  });

  const [typeForm, setTypeForm] = useState({
    name: '',
    code: '',
    description: '',
    isPaid: true,
    accrualType: 'yearly',
    accrualAmount: 0,
    maxCarryForward: 0,
    isEncashmentAllowed: false,
    maxEncashment: 0,
    applicationRules: {
      minDays: 1,
      maxDays: 0,
      includeWeekendsInCalculation: false
    }
  });

  // API hooks
  const { data: leaveRequestsData, isLoading: requestsLoading } = useGetLeaveRequestsQuery({
    search: searchTerm,
    employee: user?.role === 'employee' ? user?.employeeId : undefined,
    ...filters
  });

  const { data: leaveTypesData, isLoading: typesLoading } = useGetLeaveTypesQuery();

  const { data: leaveBalancesData, isLoading: balancesLoading } = useGetLeaveBalancesQuery({
    employeeId: user?.employeeId,
    year: new Date().getFullYear()
  });

  // Calendar API
  const { data: calendarData, isLoading: calendarLoading } = useGetLeaveCalendarQuery({
    month: currentDate.getMonth(),
    year: currentDate.getFullYear(),
    employeeId: user?.employeeId // Only show current employee's leaves
  });

  const { data: holidaysData } = useGetHolidaysForCalendarQuery({
    month: currentDate.getMonth() + 1, // API expects 1-based month
    year: currentDate.getFullYear()
  });

  const [createLeaveRequest, { isLoading: creatingRequest }] = useCreateLeaveRequestMutation();
  const [updateLeaveRequest, { isLoading: updatingRequest }] = useUpdateLeaveRequestMutation();
  const [cancelLeaveRequest, { isLoading: cancellingRequest }] = useCancelLeaveRequestMutation();
  const [approveRequest, { isLoading: approving }] = useApproveLeaveRequestMutation();
  const [rejectRequest, { isLoading: rejecting }] = useRejectLeaveRequestMutation();
  const [createLeaveType, { isLoading: creatingType }] = useCreateLeaveTypeMutation();

  const leaveRequests = leaveRequestsData?.data?.leaveRequests || [];
  const leaveTypes = leaveTypesData?.data?.leaveTypes || [];
  const leaveBalances = leaveBalancesData?.data?.balances || [];

  // Calendar data
  const calendarEvents = calendarData?.data?.events || [];
  const calendarDays = (calendarData?.data?.days || []).map(day => new Date(day));
  const holidays = holidaysData?.data || [];

  // Update current leave balance when leave type changes
  useEffect(() => {
    if (leaveForm.leaveTypeId && leaveBalances.length > 0) {
      const selectedBalance = leaveBalances.find(
        balance => balance.leaveTypeId?._id === leaveForm.leaveTypeId
      );
      setCurrentLeaveBalance(selectedBalance || null);
    } else {
      setCurrentLeaveBalance(null);
    }
  }, [leaveForm.leaveTypeId, leaveBalances]);

  // Handle clicks outside legend dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.legend-container')) {
        setShowLegend(false);
      }
    };

    if (showLegend) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showLegend]);

  // Handlers
  const handleCreateLeaveRequest = async (e) => {
    e.preventDefault();
    
    // Check balance validation for employees
    if (user?.role === 'employee' && currentLeaveBalance && currentLeaveBalance.available <= 0) {
      alert('Insufficient leave balance. You cannot create a leave request.');
      return;
    }
    
    try {
      const requestData = {
        ...leaveForm
      };
      
      // Only include employeeId for admins creating requests for other employees
      if (user?.role !== 'employee') {
        requestData.employeeId = leaveForm.employeeId;
      }
      
      if (selectedRequest) {
        // Update existing request
        await updateLeaveRequest({
          id: selectedRequest._id,
          ...requestData
        }).unwrap();
        alert('Leave request updated successfully');
      } else {
        // Create new request
        await createLeaveRequest(requestData).unwrap();
        alert('Leave request created successfully');
      }
      
      setShowAddModal(false);
      resetLeaveForm();
      setCurrentLeaveBalance(null);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error saving leave request:', error);
      alert(error?.data?.message || 'Failed to save leave request');
    }
  };

  const handleEditRequest = (request) => {
    // Pre-populate the form with existing request data
    setLeaveForm({
      leaveTypeId: request.leaveTypeId._id,
      fromDate: request.fromDate.split('T')[0], // Format for date input
      toDate: request.toDate.split('T')[0], // Format for date input
      reason: request.reason,
      contactDuringLeave: request.contactDuringLeave || {
        phone: '',
        email: '',
        address: ''
      }
    });
    setSelectedRequest(request);
    setShowAddModal(true);
  };

  const handleDeleteRequest = async (requestId) => {
    if (window.confirm('Are you sure you want to cancel this leave request?')) {
      try {
        await cancelLeaveRequest({
          id: requestId,
          reason: 'Cancelled by employee'
        }).unwrap();
        alert('Leave request cancelled successfully');
      } catch (error) {
        console.error('Error cancelling leave request:', error);
        alert(error?.data?.message || 'Failed to cancel leave request');
      }
    }
  };

  const handleApproveRequest = async (requestId) => {
    // Only allow approval for admins/managers
    if (user?.role !== 'company_admin' && user?.role !== 'manager') {
      alert('You do not have permission to approve leave requests.');
      return;
    }
    
    try {
      await approveRequest({
        id: requestId,
        remarks: 'Approved via dashboard'
      }).unwrap();
    } catch (error) {
      console.error('Error approving leave request:', error);
    }
  };

  const handleRejectRequest = async (requestId) => {
    // Only allow rejection for admins/managers
    if (user?.role !== 'company_admin' && user?.role !== 'manager') {
      alert('You do not have permission to reject leave requests.');
      return;
    }
    
    try {
      await rejectRequest({
        id: requestId,
        remarks: 'Rejected via dashboard'
      }).unwrap();
    } catch (error) {
      console.error('Error rejecting leave request:', error);
    }
  };

  const handleCreateLeaveType = async (e) => {
    e.preventDefault();
    try {
      await createLeaveType(typeForm).unwrap();
      setShowTypeModal(false);
      resetTypeForm();
    } catch (error) {
      console.error('Error creating leave type:', error);
    }
  };

  const resetLeaveForm = () => {
    setLeaveForm({
      leaveTypeId: '',
      fromDate: '',
      toDate: '',
      reason: '',
      contactDuringLeave: {
        phone: '',
        email: '',
        address: ''
      }
    });
    setSelectedRequest(null);
  };

  const resetTypeForm = () => {
    setTypeForm({
      name: '',
      code: '',
      description: '',
      isPaid: true,
      accrualType: 'yearly',
      accrualAmount: 0,
      maxCarryForward: 0,
      isEncashmentAllowed: false,
      maxEncashment: 0,
      applicationRules: {
        minDays: 1,
        maxDays: 0,
        includeWeekendsInCalculation: false
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  // Calendar helper functions
  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  const getEventsForDay = (day) => {
    // Get leave events
    const leaveEvents = calendarEvents.filter(event => {
      const eventDate = new Date(event.start);
      return eventDate.toDateString() === day.toDateString();
    });

    // Get holidays for this day
    const dayHolidays = holidays.filter(holiday => {
      const holidayDate = new Date(holiday.date);
      return holidayDate.toDateString() === day.toDateString();
    });

    // Combine and return both types of events
    return [...leaveEvents, ...dayHolidays];
  };

  const tabs = [
    { id: 'requests', label: 'My Leave Requests', icon: CalendarDaysIcon },
    { id: 'balances', label: 'My Leave Balances', icon: ChartBarIcon },
    { id: 'calendar', label: 'Leave Calendar', icon: CalendarIcon }
  ];

  return (
    <>
      <Helmet>
        <title>My Leave Management - {t('common:app_name')}</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Modern Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  My Leave Management
                </h1>
                <p className="mt-2 text-lg text-gray-600">
                  Manage your leave requests and view your balances
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="bg-white rounded-full p-3 shadow-lg">
                  <CalendarDaysIcon className="h-8 w-8 text-blue-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Modern Tabs */}
          <div className="mb-8">
            <div className="bg-white rounded-2xl shadow-xl p-2">
              <div className="flex space-x-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center px-6 py-4 rounded-xl font-semibold text-sm transition-all duration-300 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg transform scale-105'
                        : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    <tab.icon className="h-5 w-5 mr-3" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'requests' && (
            <div className="space-y-6">
              {/* Modern Header with Actions */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Leave Requests</h2>
                    <p className="text-gray-600 mt-1">Manage and track all leave applications</p>
                  </div>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    New Request
                  </button>
                </div>
              </div>

              {/* Modern Requests Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {leaveRequests.map((request) => (
                  <div key={request._id} className="bg-white rounded-3xl shadow-xl hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 overflow-hidden border border-gray-100 group">
                    {/* Status Banner */}
                    <div className={`h-2 w-full ${
                      request.status === 'approved' ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                      request.status === 'rejected' ? 'bg-gradient-to-r from-red-500 to-rose-500' :
                      request.status === 'pending' ? 'bg-gradient-to-r from-yellow-500 to-amber-500' :
                      'bg-gradient-to-r from-gray-500 to-slate-500'
                    }`}></div>

                    <div className="p-6">
                      {/* Request Header with Status Badge */}
                      <div className="flex items-start justify-between mb-5">
                        <div className="flex items-center space-x-4">
                          <div className="relative">
                            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                              <UserIcon className="h-7 w-7 text-white" />
                            </div>
                            {/* Online indicator */}
                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 border-2 border-white rounded-full"></div>
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900 leading-tight">
                              Leave Request #{request._id.slice(-6)}
                            </h3>
                            <p className="text-sm text-gray-500 font-medium">{request.employeeId.firstName} {request.employeeId.lastName}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(request.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                        <div className={`px-4 py-2 rounded-full text-xs font-bold shadow-sm border ${
                          request.status === 'approved' ? 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border-green-200' :
                          request.status === 'rejected' ? 'bg-gradient-to-r from-red-50 to-rose-50 text-red-700 border-red-200' :
                          request.status === 'pending' ? 'bg-gradient-to-r from-yellow-50 to-amber-50 text-yellow-700 border-yellow-200' :
                          'bg-gradient-to-r from-gray-50 to-slate-50 text-gray-700 border-gray-200'
                        }`}>
                          <div className="flex items-center space-x-1">
                            {request.status === 'approved' && <CheckCircleIcon className="h-3 w-3" />}
                            {request.status === 'rejected' && <XCircleIcon className="h-3 w-3" />}
                            {request.status === 'pending' && <ClockIcon className="h-3 w-3" />}
                            <span className="capitalize">{request.status}</span>
                          </div>
                        </div>
                      </div>

                      {/* Leave Details with Enhanced Design */}
                      <div className="space-y-4 mb-6">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-100 hover:shadow-md transition-shadow duration-200">
                            <div className="flex items-center space-x-2 mb-2">
                              <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center">
                                <CalendarIcon className="h-3 w-3 text-blue-600" />
                              </div>
                              <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Leave Type</span>
                            </div>
                            <p className="text-sm font-bold text-blue-900 leading-tight">{request.leaveTypeId.name}</p>
                            <p className="text-xs text-blue-600 mt-1">{request.leaveTypeId.code}</p>
                          </div>
                          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-4 border border-purple-100 hover:shadow-md transition-shadow duration-200">
                            <div className="flex items-center space-x-2 mb-2">
                              <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center">
                                <ClockIcon className="h-3 w-3 text-purple-600" />
                              </div>
                              <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Duration</span>
                            </div>
                            <p className="text-sm font-bold text-purple-900 leading-tight">
                              {request.workingDays} days
                            </p>
                            <p className="text-xs text-purple-600 mt-1">
                              {new Date(request.fromDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(request.toDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Reason Preview with Better Design */}
                      {request.reason && (
                        <div className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl border border-gray-200">
                          <div className="flex items-center space-x-2 mb-2">
                            <div className="w-6 h-6 bg-gray-100 rounded-lg flex items-center justify-center">
                              <DocumentTextIcon className="h-3 w-3 text-gray-600" />
                            </div>
                            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Reason</span>
                          </div>
                          <p className="text-sm text-gray-700 line-clamp-2 leading-relaxed">{request.reason}</p>
                        </div>
                      )}

                      {/* Enhanced Action Buttons */}
                      <div className="space-y-4">
                        {/* Primary Actions Row */}
                        {(request.status === 'pending' && (user?.role === 'company_admin' || user?.role === 'manager')) && (
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              onClick={() => handleApproveRequest(request._id)}
                              disabled={approving}
                              className="relative overflow-hidden bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-3 px-4 rounded-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 group/btn"
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 opacity-0 group-hover/btn:opacity-20 transition-opacity duration-300"></div>
                              <div className="flex items-center justify-center space-x-2 relative z-10">
                                <CheckCircleIcon className="h-5 w-5" />
                                <span className="text-sm">Approve</span>
                              </div>
                              {approving && (
                                <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                </div>
                              )}
                            </button>
                            <button
                              onClick={() => handleRejectRequest(request._id)}
                              disabled={rejecting}
                              className="relative overflow-hidden bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-bold py-3 px-4 rounded-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 group/btn"
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-red-400 to-rose-500 opacity-0 group-hover/btn:opacity-20 transition-opacity duration-300"></div>
                              <div className="flex items-center justify-center space-x-2 relative z-10">
                                <XCircleIcon className="h-5 w-5" />
                                <span className="text-sm">Reject</span>
                              </div>
                              {rejecting && (
                                <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                </div>
                              )}
                            </button>
                          </div>
                        )}

                        {/* Secondary Actions Row */}
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          <button
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowViewModal(true);
                            }}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-xl transition-all duration-200 border border-blue-200 hover:border-blue-300 hover:shadow-md"
                          >
                            <EyeIcon className="h-4 w-4 mr-2" />
                            View Details
                          </button>

                          {request.status === 'pending' && request.employeeId._id === user?.employeeId && (
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEditRequest(request)}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-xl transition-all duration-200 border border-indigo-200 hover:border-indigo-300 hover:shadow-md"
                              >
                                <PencilIcon className="h-4 w-4 mr-1" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteRequest(request._id)}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-xl transition-all duration-200 border border-red-200 hover:border-red-300 hover:shadow-md"
                              >
                                <XMarkIcon className="h-4 w-4 mr-1" />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {leaveRequests.length === 0 && (
                <div className="bg-white rounded-3xl shadow-xl p-12 text-center border border-gray-100">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CalendarDaysIcon className="h-12 w-12 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">No Leave Requests</h3>
                  <p className="text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">
                    You haven't submitted any leave requests yet. Create your first request to get started.
                  </p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-2xl hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create First Request
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'balances' && (
            <div className="space-y-6">
              {/* Modern Header */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">My Leave Balances</h2>
                    <p className="text-gray-600 mt-1">Track your available leave days</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-lg font-semibold">
                      {leaveBalances.reduce((total, balance) => total + balance.available, 0)} days available
                    </div>
                  </div>
                </div>
              </div>

              {/* Modern Balance Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {leaveBalances.map((balance) => {
                  const usagePercentage = balance.allocated > 0 ? (balance.used / balance.allocated) * 100 : 0;
                  const isLow = balance.available < 5;

                  return (
                    <div key={balance._id} className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 overflow-hidden">
                      <div className="p-6">
                        {/* Balance Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              balance.leaveTypeId.code === 'CL' ? 'bg-blue-100 text-blue-600' :
                              balance.leaveTypeId.code === 'SL' ? 'bg-red-100 text-red-600' :
                              balance.leaveTypeId.code === 'EL' ? 'bg-green-100 text-green-600' :
                              'bg-purple-100 text-purple-600'
                            }`}>
                              <CalendarDaysIcon className="h-6 w-6" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                {balance.leaveTypeId.name}
                              </h3>
                              <p className="text-sm text-gray-500">{balance.leaveTypeId.code}</p>
                            </div>
                          </div>
                          {isLow && (
                            <ExclamationTriangleIcon className="h-6 w-6 text-orange-500" />
                          )}
                        </div>

                        {/* Balance Stats */}
                        <div className="space-y-3 mb-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-500">Available</span>
                            <span className={`text-xl font-bold ${isLow ? 'text-orange-600' : 'text-green-600'}`}>
                              {balance.available} days
                            </span>
                          </div>

                          {/* Progress Bar */}
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-500 ${
                                usagePercentage > 80 ? 'bg-red-500' :
                                usagePercentage > 60 ? 'bg-yellow-500' :
                                'bg-green-500'
                              }`}
                              style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                            ></div>
                          </div>

                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="text-center">
                              <p className="text-gray-500">Allocated</p>
                              <p className="font-semibold text-gray-900">{balance.allocated}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-gray-500">Used</p>
                              <p className="font-semibold text-gray-900">{balance.used}</p>
                            </div>
                          </div>

                          {balance.carriedForward > 0 && (
                            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                              <span className="text-sm font-medium text-gray-500">Carried Forward</span>
                              <span className="text-sm font-semibold text-blue-600">+{balance.carriedForward}</span>
                            </div>
                          )}
                        </div>

                        {/* Usage Indicator */}
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">
                            {usagePercentage.toFixed(1)}% used
                          </span>
                          <div className="flex items-center space-x-1">
                            {usagePercentage > 80 ? (
                              <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />
                            ) : (
                              <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />
                            )}
                            <span className={`font-medium ${
                              usagePercentage > 80 ? 'text-red-600' :
                              usagePercentage > 60 ? 'text-yellow-600' :
                              'text-green-600'
                            }`}>
                              {usagePercentage > 80 ? 'High Usage' :
                               usagePercentage > 60 ? 'Moderate' : 'Good'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {leaveBalances.length === 0 && (
                <div className="bg-white rounded-3xl shadow-xl p-12 text-center border border-gray-100">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <ChartBarIcon className="h-12 w-12 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">No Leave Balances</h3>
                  <p className="text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">
                    Your leave balances will appear here once they are allocated by your administrator.
                  </p>
                  <button className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-2xl hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200">
                    <ChartBarIcon className="h-5 w-5 mr-2" />
                    Check Back Later
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'calendar' && (
            <div className="space-y-6">
              {/* Modern Header */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">My Leave Calendar</h2>
                    <p className="text-gray-600 mt-1">View your approved leave schedules</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-lg font-semibold">
                      {calendarEvents.length} leave events
                    </div>
                  </div>
                </div>
              </div>

              {/* Beautiful Calendar Grid */}
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
                {calendarLoading ? (
                  <div className="p-16 text-center">
                    <div className="relative">
                      <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-6"></div>
                      <div className="absolute inset-0 rounded-full border-4 border-purple-200 border-t-purple-600 animate-spin mx-auto" style={{animationDirection: 'reverse', animationDuration: '1.5s'}}></div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Loading Calendar</h3>
                    <p className="text-gray-600">Fetching your leave schedules...</p>
                  </div>
                ) : (
                  <>
                    {/* Calendar Header */}
                    <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-t-3xl">
                      <div className="relative">
                        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                        <div className="relative px-8 py-6">
                          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                            <div className="text-white">
                              <div className="flex items-center space-x-3 mb-2">
                                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center">
                                  <CalendarIcon className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                  <h1 className="text-3xl font-bold">Leave Calendar</h1>
                                  <p className="text-blue-100 text-lg">Your approved leave schedule overview</p>
                                </div>
                              </div>
                            </div>

                            {/* Navigation Controls */}
                            <div className="flex items-center justify-between bg-white bg-opacity-10 rounded-2xl p-4 backdrop-blur-sm">
                              <div className="flex items-center space-x-3">
                                <button
                                  onClick={() => navigateMonth(-1)}
                                  className="w-12 h-12 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
                                >
                                  <ChevronLeftIcon className="h-6 w-6 text-white" />
                                </button>

                                <div className="text-center px-6">
                                  <div className="text-2xl font-bold text-white">
                                    {formatMonthYear(currentDate)}
                                  </div>
                                  <button
                                    onClick={goToToday}
                                    className="mt-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg font-medium transition-all duration-200 hover:scale-105"
                                  >
                                    Today
                                  </button>
                                </div>

                                <button
                                  onClick={() => navigateMonth(1)}
                                  className="w-12 h-12 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
                                >
                                  <ChevronLeftIcon className="h-6 w-6 text-white rotate-180" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Legend Button */}
                      <div className="flex justify-end mb-4">
                        <div className="relative legend-container">
                          <button
                            onClick={() => setShowLegend(!showLegend)}
                            className="flex items-center space-x-2 px-4 py-2 bg-white hover:bg-gray-50 rounded-xl transition-all duration-200 hover:scale-105 shadow-lg border border-gray-200"
                          >
                            <div className="flex items-center space-x-1">
                              {leaveTypes.slice(0, 3).map(type => (
                                <div
                                  key={type._id}
                                  className="w-2 h-2 rounded-full shadow-sm"
                                  style={{ backgroundColor: type.color || '#3B82F6' }}
                                ></div>
                              ))}
                              {leaveTypes.length > 3 && (
                                <span className="text-xs text-gray-600 ml-1 font-medium">+{leaveTypes.length - 3}</span>
                              )}
                            </div>
                            <span className="text-sm font-semibold text-gray-700">Legend</span>
                            <ChevronDownIcon className={`h-4 w-4 text-gray-600 transition-transform ${showLegend ? 'rotate-180' : ''}`} />
                          </button>

                          {/* Legend Dropdown */}
                          {showLegend && (
                            <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border-2 border-blue-200 p-4 z-[200] animate-in slide-in-from-top-2 duration-200">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-semibold text-gray-900 flex items-center space-x-2">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                                  <span>Leave Types Legend</span>
                                </h4>
                                <button
                                  onClick={() => setShowLegend(false)}
                                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1 transition-colors"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>

                              <div className="grid grid-cols-1 gap-2 mb-3">
                                {leaveTypes.map(type => (
                                  <div
                                    key={type._id}
                                    className="flex items-center space-x-2 p-2 bg-gradient-to-r from-gray-50 to-white rounded-lg hover:from-blue-50 hover:to-indigo-50 border border-gray-100 hover:border-blue-200 transition-all duration-200 cursor-pointer group"
                                  >
                                    <div
                                      className="w-4 h-4 rounded-full shadow-md ring-2 ring-white group-hover:ring-blue-200 transition-all duration-200"
                                      style={{ backgroundColor: type.color || '#3B82F6' }}
                                    ></div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs font-semibold text-gray-900 truncate group-hover:text-blue-900">{type.name}</div>
                                      <div className="text-xs text-gray-500 font-medium">{type.code}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {/* Holiday Types Section */}
                              <div className="mb-3">
                                <h5 className="text-xs font-semibold text-gray-700 mb-2 flex items-center space-x-2">
                                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                  <span>Holiday Types</span>
                                </h5>
                                <div className="grid grid-cols-1 gap-1">
                                  {[
                                    { type: 'national', label: 'National', color: '#DC2626', icon: '🇮🇳' },
                                    { type: 'regional', label: 'Regional', color: '#EA580C', icon: '🏛️' },
                                    { type: 'company', label: 'Company', color: '#2563EB', icon: '🏢' },
                                    { type: 'religious', label: 'Religious', color: '#7C3AED', icon: '🙏' },
                                    { type: 'other', label: 'Other', color: '#059669', icon: '📅' }
                                  ].map(holidayType => (
                                    <div
                                      key={holidayType.type}
                                      className="flex items-center space-x-2 p-2 bg-gradient-to-r from-gray-50 to-white rounded-lg hover:from-green-50 hover:to-emerald-50 border border-gray-100 hover:border-green-200 transition-all duration-200 cursor-pointer group"
                                    >
                                      <div className="text-sm">{holidayType.icon}</div>
                                      <div
                                        className="w-3 h-3 rounded-full shadow-md ring-1 ring-white group-hover:ring-green-200 transition-all duration-200"
                                        style={{ backgroundColor: holidayType.color }}
                                      ></div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-xs font-semibold text-gray-900 truncate group-hover:text-green-900">{holidayType.label}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div className="pt-3 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-3">
                                <div className="text-center">
                                  <div className="text-xs">
                                    <div className="font-bold text-gray-900 text-lg">{calendarEvents.length}</div>
                                    <div className="text-gray-600 font-medium">Your Leave Events</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Calendar Header Days */}
                      <div className="grid grid-cols-7 bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
                          <div key={day} className="p-6 text-center">
                            <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">{day.slice(0, 3)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Calendar Body */}
                    <div className="grid grid-cols-7">
                      {calendarDays.map((day, index) => {
                        const dayEvents = getEventsForDay(day);
                        const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                        const isToday = day.toDateString() === new Date().toDateString();
                        const isWeekend = day.getDay() === 0 || day.getDay() === 6;

                        return (
                          <div
                            key={index}
                            className={`min-h-[140px] p-3 border-r border-b border-gray-100 relative group transition-all duration-300 ${
                              !isCurrentMonth
                                ? 'bg-gray-50 opacity-60'
                                : isToday
                                  ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'
                                  : isWeekend
                                    ? 'bg-gradient-to-br from-gray-50 to-slate-50'
                                    : 'bg-white hover:bg-gray-50'
                            }`}
                          >
                            {/* Day Number */}
                            <div className={`text-lg font-bold mb-3 transition-all duration-200 ${
                              isCurrentMonth
                                ? isToday
                                  ? 'text-blue-600 bg-blue-100 rounded-lg px-2 py-1 inline-block'
                                  : 'text-gray-900'
                                : 'text-gray-400'
                            }`}>
                              {day.getDate()}
                            </div>

                            {/* Events */}
                            <div className="space-y-2">
                              {dayEvents.slice(0, 3).map((event, eventIndex) => (
                                <div
                                  key={event.id || event._id}
                                  className="relative group/event cursor-pointer transform hover:scale-105 transition-all duration-200"
                                  onClick={() => setSelectedEvent(event)}
                                >
                                  <div
                                    className="text-xs p-2 rounded-lg shadow-sm border-l-4 hover:shadow-md transition-all duration-200"
                                    style={{
                                      backgroundColor: event.color + '15',
                                      borderLeftColor: event.color,
                                      color: event.color
                                    }}
                                  >
                                    {event.holidayType ? (
                                      // Holiday event
                                      <div>
                                        <div className="font-semibold truncate">
                                          🎉 {event.title}
                                        </div>
                                        <div className="text-xs opacity-75">{event.type}</div>
                                      </div>
                                    ) : (
                                      // Leave event
                                      <div>
                                        <div className="font-semibold truncate">{event.title}</div>
                                        <div className="text-xs opacity-75">{event.workingDays} days</div>
                                      </div>
                                    )}
                                  </div>

                                  {/* Tooltip */}
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover/event:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none">
                                    {event.holidayType ? (
                                      // Holiday tooltip
                                      <div>
                                        <div className="font-semibold">{event.title}</div>
                                        <div className="text-gray-300">{event.type} Holiday</div>
                                        {event.description && (
                                          <div className="text-gray-400 text-xs mt-1">{event.description}</div>
                                        )}
                                      </div>
                                    ) : (
                                      // Leave tooltip
                                      <div>
                                        {event.title}
                                        <div className="text-gray-300">{event.workingDays} working days</div>
                                      </div>
                                    )}
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                  </div>
                                </div>
                              ))}

                              {/* Show more indicator */}
                              {dayEvents.length > 3 && (
                                <div className="text-xs text-gray-500 text-center py-1">
                                  +{dayEvents.length - 3} more
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* View Leave Request Modal */}
      {showViewModal && selectedRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-start justify-center min-h-screen pt-4 px-4 pb-4">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"></div>
            <div className="relative bg-white rounded-3xl shadow-2xl transform transition-all max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto mt-8">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-xl bg-white bg-opacity-20 flex items-center justify-center">
                      <DocumentTextIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Leave Request Details</h3>
                      <p className="text-blue-100 text-sm">Application #{selectedRequest.applicationNumber || selectedRequest._id.slice(-6)}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowViewModal(false);
                      setSelectedRequest(null);
                    }}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="px-8 py-6">
                <div className="space-y-6">
                  {/* Employee Information */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Employee Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm font-medium text-gray-500">Employee Name</span>
                        <p className="text-sm font-semibold text-gray-900">
                          {selectedRequest.employeeId?.firstName} {selectedRequest.employeeId?.lastName}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Employee ID</span>
                        <p className="text-sm font-semibold text-gray-900">{selectedRequest.employeeId?.employeeId}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Email</span>
                        <p className="text-sm font-semibold text-gray-900">{selectedRequest.employeeId?.email}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Phone</span>
                        <p className="text-sm font-semibold text-gray-900">{selectedRequest.employeeId?.phone}</p>
                      </div>
                    </div>
                  </div>

                  {/* Leave Details */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Leave Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm font-medium text-gray-500">Leave Type</span>
                        <p className="text-sm font-semibold text-gray-900">
                          {selectedRequest.leaveTypeId?.name} ({selectedRequest.leaveTypeId?.code})
                        </p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Status</span>
                        <div className="mt-1">
                          <span className={`inline-flex items-center px-3 py-1 text-sm font-bold rounded-full ${
                            selectedRequest.status === 'approved' ? 'bg-green-100 text-green-800 border-2 border-green-200' :
                            selectedRequest.status === 'rejected' ? 'bg-red-100 text-red-800 border-2 border-red-200' :
                            selectedRequest.status === 'pending' ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-200' :
                            'bg-gray-100 text-gray-800 border-2 border-gray-200'
                          }`}>
                            {selectedRequest.status === 'approved' && <CheckCircleIcon className="h-4 w-4 mr-1" />}
                            {selectedRequest.status === 'rejected' && <XCircleIcon className="h-4 w-4 mr-1" />}
                            {selectedRequest.status === 'pending' && <ClockIcon className="h-4 w-4 mr-1" />}
                            {selectedRequest.status.charAt(0).toUpperCase() + selectedRequest.status.slice(1)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">From Date</span>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatDate(selectedRequest.fromDate)}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">To Date</span>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatDate(selectedRequest.toDate)}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Total Days</span>
                        <p className="text-sm font-semibold text-gray-900">{selectedRequest.totalDays}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Working Days</span>
                        <p className="text-sm font-semibold text-gray-900">{selectedRequest.workingDays}</p>
                      </div>
                    </div>
                  </div>

                  {/* Reason */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Reason</h4>
                    <p className="text-sm text-gray-700">{selectedRequest.reason}</p>
                  </div>

                  {/* Contact Information */}
                  {selectedRequest.contactDuringLeave && (
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Contact During Leave</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <span className="text-sm font-medium text-gray-500">Phone</span>
                          <p className="text-sm font-semibold text-gray-900">{selectedRequest.contactDuringLeave.phone}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Email</span>
                          <p className="text-sm font-semibold text-gray-900">{selectedRequest.contactDuringLeave.email}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Address</span>
                          <p className="text-sm font-semibold text-gray-900">{selectedRequest.contactDuringLeave.address}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Approval Chain */}
                  {selectedRequest.approvalChain && selectedRequest.approvalChain.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                          <UsersIcon className="h-4 w-4 text-blue-600" />
                        </div>
                        Approval Status
                        {selectedRequest.approvalChain.some(approval => approval.remarks) && (
                          <span className="ml-2 inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            <ChatBubbleLeftRightIcon className="h-3 w-3 mr-1" />
                            Has Remarks
                          </span>
                        )}
                      </h4>
                      <div className="space-y-3">
                        {selectedRequest.approvalChain.map((approval, index) => (
                          <div key={index} className="p-4 bg-white rounded-xl border border-gray-200 hover:shadow-md transition-shadow duration-200">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center space-x-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  approval.status === 'approved' ? 'bg-green-100' :
                                  approval.status === 'rejected' ? 'bg-red-100' :
                                  'bg-yellow-100'
                                }`}>
                                  {approval.status === 'approved' ? (
                                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                  ) : approval.status === 'rejected' ? (
                                    <XCircleIcon className="h-5 w-5 text-red-600" />
                                  ) : (
                                    <ClockIcon className="h-5 w-5 text-yellow-600" />
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-bold text-gray-900">{approval.approverName}</p>
                                  <p className="text-xs text-gray-500">{approval.approverDesignation}</p>
                                </div>
                              </div>
                              <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                                approval.status === 'approved' ? 'bg-green-100 text-green-800 border border-green-200' :
                                approval.status === 'rejected' ? 'bg-red-100 text-red-800 border border-red-200' :
                                'bg-yellow-100 text-yellow-800 border border-yellow-200'
                              }`}>
                                {approval.status.charAt(0).toUpperCase() + approval.status.slice(1)}
                              </span>
                            </div>
                            {approval.decidedAt && (
                              <p className="text-xs text-gray-500 mb-2 flex items-center">
                                <ClockIcon className="h-3 w-3 mr-1" />
                                {formatDateTime(approval.decidedAt)}
                              </p>
                            )}
                            {approval.remarks && (
                              <div className="mt-3 p-3 bg-gray-50 rounded-lg border-l-4 border-gray-300">
                                <p className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                  <ChatBubbleLeftRightIcon className="h-3 w-3 mr-1" />
                                  Remarks:
                                </p>
                                <p className="text-sm text-gray-600 leading-relaxed">{approval.remarks}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Final Decision */}
                  {selectedRequest.finalDecision && (
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                          selectedRequest.finalDecision.status === 'approved' ? 'bg-green-100' : 'bg-red-100'
                        }`}>
                          {selectedRequest.finalDecision.status === 'approved' ? (
                            <CheckCircleIcon className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircleIcon className="h-4 w-4 text-red-600" />
                          )}
                        </div>
                        Final Decision
                      </h4>
                      <div className="p-4 bg-white rounded-xl border border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-sm font-medium text-gray-900">Decision</span>
                          <span className={`inline-flex items-center px-4 py-2 text-sm font-bold rounded-full ${
                            selectedRequest.finalDecision.status === 'approved' 
                              ? 'bg-green-100 text-green-800 border-2 border-green-200' 
                              : 'bg-red-100 text-red-800 border-2 border-red-200'
                          }`}>
                            {selectedRequest.finalDecision.status === 'approved' ? (
                              <CheckCircleIcon className="h-4 w-4 mr-2" />
                            ) : (
                              <XCircleIcon className="h-4 w-4 mr-2" />
                            )}
                            {selectedRequest.finalDecision.status.charAt(0).toUpperCase() + selectedRequest.finalDecision.status.slice(1)}
                          </span>
                        </div>
                        {selectedRequest.finalDecision.decidedAt && (
                          <div className="mb-4">
                            <span className="text-sm font-medium text-gray-500">Decision Date:</span>
                            <p className="text-sm text-gray-900 flex items-center mt-1">
                              <ClockIcon className="h-4 w-4 mr-2 text-gray-400" />
                              {formatDateTime(selectedRequest.finalDecision.decidedAt)}
                            </p>
                          </div>
                        )}
                        {selectedRequest.finalDecision.remarks && (
                          <div className="mt-4 p-4 bg-gray-50 rounded-lg border-l-4 border-gray-300">
                            <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                              <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                              Decision Remarks:
                            </p>
                            <p className="text-sm text-gray-600 leading-relaxed">{selectedRequest.finalDecision.remarks}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Request Timeline */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Request Timeline</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <PlusIcon className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">Request Created</div>
                          <div className="text-xs text-gray-500">
                            {formatDateTime(selectedRequest.createdAt)}
                          </div>
                        </div>
                      </div>

                      {selectedRequest.status !== 'pending' && (
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            selectedRequest.status === 'approved' ? 'bg-green-100' : 'bg-red-100'
                          }`}>
                            {selectedRequest.status === 'approved' ? (
                              <CheckCircleIcon className="h-4 w-4 text-green-600" />
                            ) : (
                              <XCircleIcon className="h-4 w-4 text-red-600" />
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              Request {selectedRequest.status === 'approved' ? 'Approved' : 'Rejected'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {selectedRequest.finalDecision?.decidedAt 
                                ? formatDateTime(selectedRequest.finalDecision.decidedAt)
                                : selectedRequest.approvalChain && selectedRequest.approvalChain.length > 0
                                  ? formatDateTime(selectedRequest.approvalChain[selectedRequest.approvalChain.length - 1].decidedAt)
                                  : selectedRequest.processedAt 
                                    ? formatDateTime(selectedRequest.processedAt)
                                    : 'Date not available'
                              }
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-8 py-6 rounded-b-3xl flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedRequest(null);
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold rounded-xl hover:from-gray-700 hover:to-gray-800 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modern Create Leave Request Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-start justify-center min-h-screen pt-4 px-4 pb-4">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"></div>
            <div className="relative bg-white rounded-3xl shadow-2xl transform transition-all max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto mt-8">
              <form onSubmit={handleCreateLeaveRequest}>
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6 rounded-t-3xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                        <PlusIcon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">
                          {selectedRequest ? 'Edit Leave Request' : 'Create Leave Request'}
                        </h3>
                        <p className="text-blue-100 text-sm">
                          {selectedRequest ? 'Update your leave application' : 'Submit a new leave application'}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddModal(false);
                        resetLeaveForm();
                      }}
                      className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="px-8 py-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Leave Type */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Leave Type *
                      </label>
                      <select
                        value={leaveForm.leaveTypeId}
                        onChange={(e) => setLeaveForm(prev => ({ ...prev, leaveTypeId: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        required
                      >
                        <option value="">Select leave type</option>
                        {leaveTypes.map((type) => (
                          <option key={type._id} value={type._id}>
                            {type.name} ({type.code})
                          </option>
                        ))}
                      </select>
                      
                      {/* Balance Information */}
                      {currentLeaveBalance && (
                        <div className={`mt-3 p-3 rounded-lg ${
                          currentLeaveBalance.available > 0 
                            ? 'bg-green-50 border border-green-200' 
                            : 'bg-red-50 border border-red-200'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div className="text-sm">
                              <span className="font-medium">Available Balance:</span>
                              <span className={`ml-2 font-semibold ${currentLeaveBalance.available > 0 ? 'text-green-700' : 'text-red-700'}`}>
                                {currentLeaveBalance.available} days
                              </span>
                            </div>
                            {currentLeaveBalance.available <= 0 && (
                              <div className="text-red-600 text-sm font-medium flex items-center">
                                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                                Insufficient Balance
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* From Date */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        From Date *
                      </label>
                      <input
                        type="date"
                        value={leaveForm.fromDate}
                        onChange={(e) => setLeaveForm(prev => ({ ...prev, fromDate: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        required
                      />
                    </div>

                    {/* To Date */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        To Date *
                      </label>
                      <input
                        type="date"
                        value={leaveForm.toDate}
                        onChange={(e) => setLeaveForm(prev => ({ ...prev, toDate: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        required
                      />
                    </div>

                    {/* Reason */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Reason *
                      </label>
                      <textarea
                        value={leaveForm.reason}
                        onChange={(e) => setLeaveForm(prev => ({ ...prev, reason: e.target.value }))}
                        rows={4}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none"
                        placeholder="Please provide a detailed reason for your leave request..."
                        required
                      />
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="bg-gray-50 rounded-2xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Contact Information (Optional)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={leaveForm.contactDuringLeave.phone}
                          onChange={(e) => setLeaveForm(prev => ({
                            ...prev,
                            contactDuringLeave: { ...prev.contactDuringLeave, phone: e.target.value }
                          }))}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                          placeholder="+91 XXXXX XXXXX"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          value={leaveForm.contactDuringLeave.email}
                          onChange={(e) => setLeaveForm(prev => ({
                            ...prev,
                            contactDuringLeave: { ...prev.contactDuringLeave, email: e.target.value }
                          }))}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                          placeholder="your.email@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Address
                        </label>
                        <input
                          type="text"
                          value={leaveForm.contactDuringLeave.address}
                          onChange={(e) => setLeaveForm(prev => ({
                            ...prev,
                            contactDuringLeave: { ...prev.contactDuringLeave, address: e.target.value }
                          }))}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                          placeholder="Your location during leave"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="bg-gray-50 px-8 py-6 rounded-b-3xl flex items-center justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      resetLeaveForm();
                    }}
                    className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creatingRequest || updatingRequest || (user?.role === 'employee' && currentLeaveBalance && currentLeaveBalance.available <= 0)}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg"
                  >
                    {(creatingRequest || updatingRequest) ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {selectedRequest ? 'Updating...' : 'Creating...'}
                      </div>
                    ) : user?.role === 'employee' && currentLeaveBalance && currentLeaveBalance.available <= 0 ? (
                      'Insufficient Balance'
                    ) : (
                      selectedRequest ? 'Update Request' : 'Create Request'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Event Details Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm" onClick={() => setSelectedEvent(null)}></div>

            <div className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full mx-4 transform transition-all duration-300 scale-100">
              {/* Modal Header with Gradient */}
              <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-3xl px-8 py-6">
                <div className="absolute inset-0 bg-black bg-opacity-10"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-2xl bg-white bg-opacity-20 flex items-center justify-center shadow-lg">
                      {selectedEvent.holidayType ? (
                        <CalendarDaysIcon className="h-6 w-6 text-white" />
                      ) : (
                        <CalendarIcon className="h-6 w-6 text-white" />
                      )}
                    </div>
                    <div>
                      {selectedEvent.holidayType ? (
                        <>
                          <h3 className="text-xl font-bold text-white">Holiday Details</h3>
                          <p className="text-blue-100">{selectedEvent.type} Holiday</p>
                        </>
                      ) : (
                        <>
                          <h3 className="text-xl font-bold text-white">Leave Details</h3>
                          <p className="text-blue-100">{selectedEvent.leaveTypeName}</p>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedEvent(null)}
                    className="w-10 h-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-110"
                  >
                    <XMarkIcon className="h-5 w-5 text-white" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="px-8 py-6">
                {selectedEvent.holidayType ? (
                  // Holiday Modal Content
                  <div className="space-y-6">
                    {/* Holiday Date */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-100 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <CalendarIcon className="h-4 w-4 text-green-600" />
                        <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">Holiday Date</span>
                      </div>
                      <p className="text-sm font-bold text-green-900">
                        {new Date(selectedEvent.date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    </div>

                    {/* Holiday Type */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-100 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <CalendarDaysIcon className="h-4 w-4 text-blue-600" />
                        <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Holiday Type</span>
                      </div>
                      <p className="text-sm font-bold text-blue-900 capitalize">{selectedEvent.type}</p>
                    </div>

                    {/* Holiday Description */}
                    {selectedEvent.description && (
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <DocumentTextIcon className="h-4 w-4 text-purple-600" />
                          <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Description</span>
                        </div>
                        <p className="text-sm text-purple-900 leading-relaxed">{selectedEvent.description}</p>
                      </div>
                    )}

                    {/* Holiday Status */}
                    <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <CheckCircleIcon className="h-4 w-4 text-gray-600" />
                        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Status</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${selectedEvent.isActive ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedEvent.isActive ? 'Active' : 'Inactive'}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Leave Modal Content
                  <div className="space-y-6">
                    {/* Date Range */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <CalendarIcon className="h-4 w-4 text-blue-600" />
                          <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">From</span>
                        </div>
                        <p className="text-sm font-bold text-blue-900">
                          {new Date(selectedEvent.start).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      </div>

                      <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <CalendarIcon className="h-4 w-4 text-green-600" />
                          <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">To</span>
                        </div>
                        <p className="text-sm font-bold text-green-900">
                          {new Date(selectedEvent.end).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>

                    {/* Duration */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <ClockIcon className="h-4 w-4 text-purple-600" />
                        <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Duration</span>
                      </div>
                      <p className="text-lg font-bold text-purple-900">{selectedEvent.workingDays} Working Days</p>
                    </div>

                    {/* Leave Type */}
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <DocumentTextIcon className="h-4 w-4 text-indigo-600" />
                        <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Leave Type</span>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-indigo-900"><span className="font-medium">Type:</span> {selectedEvent.leaveTypeName}</p>
                        <p className="text-sm text-indigo-900"><span className="font-medium">Code:</span> {selectedEvent.leaveTypeCode}</p>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <CheckCircleIcon className="h-4 w-4 text-emerald-600" />
                        <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Status</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-3 py-1 text-sm font-bold rounded-full bg-emerald-100 text-emerald-800">
                          Approved
                        </span>
                      </div>
                    </div>

                    {/* Reason */}
                    {selectedEvent.reason && (
                      <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <DocumentTextIcon className="h-4 w-4 text-gray-600" />
                          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Reason</span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{selectedEvent.reason}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LeaveManagement;
